import React, { useRef, useEffect, useState, useCallback, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useGLTF, Sky, useProgress } from '@react-three/drei';
import * as THREE from 'three';
import { applyTextureOptimizations, disposeScene, resolveAssetUrl, getNetworkQuality } from '../../../services/assetCacheService';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

// Configure DRACOLoader for compressed GLTF/GLB models
const dracoLoader = new DRACOLoader();
dracoLoader?.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
dracoLoader?.preload();

// Register DRACOLoader with useGLTF's internal GLTFLoader
useGLTF?.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

// Patch: ensure any GLTFLoader instance created by drei has the DRACOLoader
const _origGLTFLoader = GLTFLoader?.prototype?.parse;
GLTFLoader.prototype.parse = function (data, path, onLoad, onError) {
  if (!this.dracoLoader) {
    this.setDRACOLoader(dracoLoader);
  }
  return _origGLTFLoader?.call(this, data, path, onLoad, onError);
};

// ─── Haptic Feedback Utility ──────────────────────────────────────────────────
const haptic = (() => {
  const isIOS = /iPhone|iPad|iPod/i?.test(navigator.userAgent);
  const hasVibration = typeof navigator.vibrate === 'function';

  let audioCtx = null;
  const iosHaptic = (type) => {
    try {
      if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = audioCtx?.createOscillator();
      const gain = audioCtx?.createGain();
      osc?.connect(gain);
      gain?.connect(audioCtx?.destination);
      gain?.gain?.setValueAtTime(0.001, audioCtx?.currentTime); 
      osc?.frequency?.setValueAtTime(type === 'light' ? 200 : type === 'medium' ? 150 : 100, audioCtx?.currentTime);
      osc?.start(audioCtx?.currentTime);
      osc?.stop(audioCtx?.currentTime + (type === 'light' ? 0.02 : type === 'medium' ? 0.04 : 0.08));
    } catch (_) {}
  };

  return {
    light: () => {
      if (hasVibration) navigator.vibrate(10);
      else if (isIOS) iosHaptic('light');
    },
    medium: () => {
      if (hasVibration) navigator.vibrate(25);
      else if (isIOS) iosHaptic('medium');
    },
    heavy: () => {
      if (hasVibration) navigator.vibrate([30, 10, 30]);
      else if (isIOS) iosHaptic('heavy');
    },
    selection: () => {
      if (hasVibration) navigator.vibrate(8);
      else if (isIOS) iosHaptic('light');
    },
  };
})();

// ─── Elegant Loading Screen (outside Canvas) ──────────────────────────────────
const LoadingScreen = ({ progress, isVisible }) => {
  const [opacity, setOpacity] = useState(1);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setOpacity(0);
      const t = setTimeout(() => setHidden(true), 600);
      return () => clearTimeout(t);
    } else {
      setHidden(false);
      setOpacity(1);
    }
  }, [isVisible]);

  if (hidden) return null;

  const pct = Math.round(progress);
  const bars = 12;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 200,
        background: 'linear-gradient(135deg, #050505 0%, #111111 50%, #050505 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity,
        transition: 'opacity 0.6s ease',
        pointerEvents: isVisible ? 'all' : 'none',
      }}
    >
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(rgba(212,175,55,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.05) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        width: '400px', height: '400px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      
      <div style={{ position: 'relative', marginBottom: '40px' }}>
        <div style={{
          position: 'absolute',
          inset: '-24px',
          borderRadius: '50%',
          border: '1px solid rgba(212,175,55,0.15)',
          animation: 'ring-spin-slow 6s linear infinite',
        }} />
        <div style={{
          position: 'absolute',
          inset: '-12px',
          borderRadius: '50%',
          border: '2px dashed rgba(212,175,55,0.25)',
          animation: 'ring-spin-reverse 4s linear infinite',
        }} />
        <div style={{
          width: '72px', height: '72px',
          borderRadius: '50%',
          border: '3px solid rgba(212,175,55,0.15)',
          borderTopColor: '#D4AF37',
          borderRightColor: 'rgba(212,175,55,0.5)',
          animation: 'ring-spin 1s cubic-bezier(0.4,0,0.2,1) infinite',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '24px',
        }}>
          🏠
        </div>
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#D4AF37', boxShadow: '0 0 8px #D4AF37', animation: 'pulse-dot 1.5s ease-in-out infinite' }} />
        <span style={{
          color: '#D4AF37', fontSize: '11px', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, letterSpacing: '0.2em',
        }}>
          KPS CREATOR ENGINE
        </span>
        <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#D4AF37', boxShadow: '0 0 8px #D4AF37', animation: 'pulse-dot 1.5s ease-in-out infinite 0.5s' }} />
      </div>
      
      <p style={{
        color: 'rgba(255,255,255,0.4)', fontSize: '13px', fontFamily: 'DM Sans, sans-serif', marginBottom: '36px', letterSpacing: '0.05em',
      }}>
        Décryptage du modèle 3D sécurisé…
      </p>
      
      <div style={{ width: '240px', marginBottom: '12px' }}>
        <div style={{ width: '100%', height: '3px', background: 'rgba(212,175,55,0.15)', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${pct}%`,
            background: 'linear-gradient(90deg, #997A15, #D4AF37, #FDE047)',
            borderRadius: '2px', transition: 'width 0.3s ease',
            boxShadow: '0 0 10px rgba(212,175,55,0.6)',
          }} />
        </div>
      </div>
      
      <div style={{
        color: 'rgba(212,175,55,0.9)', fontSize: '28px', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '4px', fontVariantNumeric: 'tabular-nums',
      }}>
        {pct}%
      </div>
      
      <div style={{ display: 'flex', gap: '4px', marginTop: '20px' }}>
        {Array.from({ length: bars })?.map((_, i) => (
          <div
            key={i}
            style={{
              width: '14px', height: '4px', borderRadius: '2px',
              background: i < Math.floor((pct / 100) * bars) ? 'rgba(212,175,55,0.9)' : 'rgba(212,175,55,0.15)',
              transition: 'background 0.2s ease',
            }}
          />
        ))}
      </div>
      
      <p style={{
        color: 'rgba(255,255,255,0.25)', fontSize: '11px', fontFamily: 'DM Sans, sans-serif', marginTop: '32px', letterSpacing: '0.04em', textAlign: 'center', maxWidth: '260px', lineHeight: 1.6,
      }}>
        Utilisez ZQSD / flèches pour vous déplacer · Clic souris pour regarder
      </p>
      <style>{`
        @keyframes ring-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes ring-spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes ring-spin-reverse { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
        @keyframes pulse-dot { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.4; transform:scale(0.7); } }
      `}</style>
    </div>
  );
};

// ─── Progress tracker (inside Canvas via R3F) ─────────────────────────────────
const ProgressTracker = ({ onProgress, onLoaded }) => {
  const { progress, active } = useProgress();

  useEffect(() => {
    onProgress(progress);
    if (!active && progress >= 100) {
      onLoaded();
    }
  }, [progress, active, onProgress, onLoaded]);

  return null;
};

// ─── Performance Monitor (inside Canvas) ─────────────────────────────────────
const PerformanceMonitor = ({ onMetrics }) => {
  const frameCount = useRef(0);
  const lastTime = useRef(performance.now());

  useFrame(() => {
    frameCount.current += 1;
    const now = performance.now();
    const elapsed = now - lastTime?.current;
    if (elapsed >= 1000) {
      const fps = Math.round((frameCount?.current * 1000) / elapsed);
      const memMB = performance?.memory
        ? Math.round(performance.memory?.usedJSHeapSize / 1048576)
        : null;
      onMetrics({ fps, memMB });
      frameCount.current = 0;
      lastTime.current = now;
    }
  });

  return null;
};

// ─── Mobile Performance Overlay ───────────────────────────────────────────────
const MobilePerformanceOverlay = ({ fps, memMB, networkQuality, modelLoadTime }) => {
  const [visible, setVisible] = useState(false);

  const fpsColor = fps === null ? '#888' : fps >= 50 ? '#4ade80' : fps >= 30 ? '#facc15' : '#f87171';
  const netColor = networkQuality === 'fast' ? '#4ade80' : networkQuality === 'medium' ? '#facc15' : '#f87171';
  const netLabel = networkQuality === 'fast' ? 'Rapide' : networkQuality === 'medium' ? 'Moyen' : networkQuality === 'slow' ? 'Lent' : '—';

  return (
    <>
      <button
        onClick={() => setVisible(v => !v)}
        style={{
          position: 'absolute', bottom: '80px', left: '12px', width: '36px', height: '36px',
          borderRadius: '50%', background: 'rgba(5,5,5,0.85)',
          border: `1px solid ${visible ? 'rgba(212,175,55,0.6)' : 'rgba(255,255,255,0.15)'}`,
          color: visible ? '#D4AF37' : 'rgba(255,255,255,0.5)',
          fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', backdropFilter: 'blur(12px)', zIndex: 60, transition: 'all 0.3s ease',
        }}
      >
        📊
      </button>
      {visible && (
        <div
          style={{
            position: 'absolute', bottom: '124px', left: '12px',
            background: 'rgba(5,5,5,0.92)', border: '1px solid rgba(212,175,55,0.3)',
            borderRadius: '12px', padding: '12px 16px', backdropFilter: 'blur(16px)',
            zIndex: 60, minWidth: '170px', pointerEvents: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
          }}
        >
          <div style={{ color: 'rgba(212,175,55,0.8)', fontSize: '9px', letterSpacing: '0.15em', fontFamily: 'DM Sans, sans-serif', marginBottom: '10px', textTransform: 'uppercase', fontWeight: 'bold' }}>
            Diagnostics
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '10px', fontFamily: 'DM Sans, sans-serif' }}>FPS</span>
            <span style={{ color: fpsColor, fontSize: '12px', fontFamily: 'DM Mono, monospace', fontWeight: 700 }}>{fps !== null ? fps : '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '10px', fontFamily: 'DM Sans, sans-serif' }}>Réseau</span>
            <span style={{ color: netColor, fontSize: '10px', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>{netLabel}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '10px', fontFamily: 'DM Sans, sans-serif' }}>Chargement</span>
            <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '10px', fontFamily: 'DM Mono, monospace' }}>{modelLoadTime !== null ? `${(modelLoadTime / 1000)?.toFixed(1)}s` : '—'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '10px', fontFamily: 'DM Sans, sans-serif' }}>Mémoire</span>
            <span style={{ color: memMB !== null ? (memMB > 300 ? '#f87171' : memMB > 150 ? '#facc15' : '#4ade80') : '#888', fontSize: '10px', fontFamily: 'DM Mono, monospace' }}>{memMB !== null ? `${memMB} MB` : 'N/A'}</span>
          </div>
        </div>
      )}
    </>
  );
};

// ─── GLB Model Loader ─────────────────────────────────────────────────────────
const GLBModel = ({ url, onLoaded, renderQuality }) => {
  const { scene } = useGLTF(url);

  useEffect(() => {
    if (scene) {
      scene?.traverse((child) => {
        if (child?.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          if (child?.material) {
            if (child?.material?.envMapIntensity === undefined) {
              child.material.envMapIntensity = 1;
            }
            if (child?.material?.isMeshStandardMaterial) {
              if (isNaN(child?.material?.roughness)) child.material.roughness = 0.5;
              if (isNaN(child?.material?.metalness)) child.material.metalness = 0;
            }
            child.material.needsUpdate = true;
          }
        }
      });
      applyTextureOptimizations(scene, renderQuality || 'medium');
      onLoaded && onLoaded(scene);
    }
  }, [scene, onLoaded, renderQuality]);

  return <primitive object={scene} />;
};

// ─── Door System ──────────────────────────────────────────────────────────────
const DoorSystem = ({ modelScene, onDoorHint }) => {
  const { camera, raycaster } = useThree();
  const doors = useRef([]);
  const interactCooldown = useRef(false);
  const DOOR_KEYWORDS = /door|porte|gate|portail|entree|entrance/i;
  const INTERACT_DISTANCE = 3.5;

  useEffect(() => {
    if (!modelScene) return;
    const found = [];
    modelScene?.traverse((child) => {
      if (child?.isMesh && DOOR_KEYWORDS?.test(child?.name)) {
        const pivot = new THREE.Group();
        const worldPos = new THREE.Vector3();
        child?.getWorldPosition(worldPos);

        const box = new THREE.Box3()?.setFromObject(child);
        const hingeX = box?.min?.x;
        const hingeZ = (box?.min?.z + box?.max?.z) / 2;

        pivot?.position?.set(hingeX, worldPos?.y, hingeZ);
        child?.parent?.add(pivot);
        pivot?.add(child);

        child.position.x -= hingeX - worldPos?.x;
        child.position.z -= hingeZ - worldPos?.z;

        found?.push({
          mesh: child, pivot, isOpen: false, currentAngle: 0, targetAngle: 0, worldPos: worldPos?.clone(),
        });
      }
    });
    doors.current = found;
  }, [modelScene]);

  useFrame((_, delta) => {
    let nearDoor = false;
    doors?.current?.forEach((door) => {
      const diff = door?.targetAngle - door?.currentAngle;
      if (Math.abs(diff) > 0.001) {
        door.currentAngle += diff * Math.min(delta * 6, 1);
        door.pivot.rotation.y = door?.currentAngle;
      }
      const camPos = camera?.position;
      door?.worldPos?.setFromMatrixPosition(door?.pivot?.matrixWorld);
      const dist = camPos?.distanceTo(door?.worldPos);
      if (dist < INTERACT_DISTANCE) nearDoor = true;
    });
    onDoorHint(nearDoor);
  });

  const tryOpenDoor = useCallback(() => {
    if (interactCooldown?.current) return;
    raycaster?.setFromCamera(new THREE.Vector2(0, 0), camera);
    const meshes = doors?.current?.map(d => d?.mesh);
    const hits = raycaster?.intersectObjects(meshes, false);

    if (hits?.length > 0 && hits?.[0]?.distance < INTERACT_DISTANCE) {
      const hitMesh = hits?.[0]?.object;
      const door = doors?.current?.find(d => d?.mesh === hitMesh);
      if (door) {
        door.isOpen = !door?.isOpen;
        door.targetAngle = door?.isOpen ? Math.PI / 2 : 0;
        interactCooldown.current = true;
        setTimeout(() => { interactCooldown.current = false; }, 600);
      }
    } else {
      let nearest = null;
      let nearestDist = INTERACT_DISTANCE;
      doors?.current?.forEach((door) => {
        const dist = camera?.position?.distanceTo(door?.worldPos);
        if (dist < nearestDist) { nearest = door; nearestDist = dist; }
      });
      if (nearest) {
        nearest.isOpen = !nearest?.isOpen;
        nearest.targetAngle = nearest?.isOpen ? Math.PI / 2 : 0;
        interactCooldown.current = true;
        setTimeout(() => { interactCooldown.current = false; }, 600);
      }
    }
  }, [camera, raycaster]);

  useEffect(() => {
    window.__viewer3d_openDoor = tryOpenDoor;
    return () => { delete window.__viewer3d_openDoor; };
  }, [tryOpenDoor]);

  return null;
};

// ─── FPS Camera Controller ────────────────────────────────────────────────────
const FPSController = ({ moveState, modelScene }) => {
  const { camera, gl } = useThree();
  const yaw = useRef(0);
  const pitch = useRef(0);
  const isPointerLocked = useRef(false);
  const modelBounds = useRef(null);
  const eyeHeight = 1.7;

  useEffect(() => {
    if (!modelScene) return;
    const modelBox = new THREE.Box3()?.setFromObject(modelScene);
    modelBounds.current = modelBox;
    const center = new THREE.Vector3();
    modelBox?.getCenter(center);
    camera?.position?.set(center?.x, modelBox?.min?.y + eyeHeight, center?.z);
    yaw.current = 0; pitch.current = 0;
  }, [modelScene, camera]);

  useEffect(() => {
    const canvas = gl?.domElement;
    const onMouseMove = (e) => {
      if (!isPointerLocked?.current) return;
      yaw.current -= e?.movementX * 0.002;
      pitch.current -= e?.movementY * 0.002;
      pitch.current = Math.max(-Math.PI / 2.2, Math.min(Math.PI / 2.2, pitch?.current));
    };
    const onLockChange = () => { isPointerLocked.current = document.pointerLockElement === canvas; };
    const onClick = () => { if (!isPointerLocked?.current) canvas?.requestPointerLock(); };

    canvas?.addEventListener('click', onClick);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('pointerlockchange', onLockChange);

    return () => {
      canvas?.removeEventListener('click', onClick);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('pointerlockchange', onLockChange);
    };
  }, [gl]);

  useEffect(() => {
    let lastTouch = null;
    const canvas = gl?.domElement;

    const onTouchStart = (e) => {
      const touch = e?.changedTouches?.[0];
      if (touch && touch?.clientX > window.innerWidth / 2) {
        lastTouch = { x: touch?.clientX, y: touch?.clientY, id: touch?.identifier };
      }
    };

    const onTouchMove = (e) => {
      if (!lastTouch) return;
      const touch = Array.from(e?.changedTouches)?.find(t => t?.identifier === lastTouch?.id);
      if (!touch) return;
      const dx = touch?.clientX - lastTouch?.x;
      const dy = touch?.clientY - lastTouch?.y;
      yaw.current -= dx * 0.006;
      pitch.current -= dy * 0.006;
      pitch.current = Math.max(-Math.PI / 2.2, Math.min(Math.PI / 2.2, pitch?.current));
      lastTouch = { x: touch?.clientX, y: touch?.clientY, id: touch?.identifier };
    };

    const onTouchEnd = (e) => {
      if (!lastTouch) return;
      const touch = Array.from(e?.changedTouches)?.find(t => t?.identifier === lastTouch?.id);
      if (touch) lastTouch = null;
    };

    canvas?.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas?.addEventListener('touchmove', onTouchMove, { passive: true });
    canvas?.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      canvas?.removeEventListener('touchstart', onTouchStart);
      canvas?.removeEventListener('touchmove', onTouchMove);
      canvas?.removeEventListener('touchend', onTouchEnd);
    };
  }, [gl]);

  useFrame((_, delta) => {
    const speed = 1.2;
    const verticalSpeed = 1.0;
    const clampedDelta = Math.min(delta, 0.05);

    camera.rotation.order = 'YXZ';
    camera.rotation.y = yaw?.current;
    camera.rotation.x = pitch?.current;

    const { forward, backward, left, right, up, down } = moveState?.current;

    if (!forward && !backward && !left && !right && !up && !down) return;

    const frontVec = new THREE.Vector3();
    camera?.getWorldDirection(frontVec);
    frontVec.y = 0; frontVec?.normalize();

    const rightVec = new THREE.Vector3();
    rightVec?.crossVectors(frontVec, new THREE.Vector3(0, 1, 0))?.normalize();

    const moveDir = new THREE.Vector3();
    if (forward) moveDir?.addScaledVector(frontVec, 1);
    if (backward) moveDir?.addScaledVector(frontVec, -1);
    if (left) moveDir?.addScaledVector(rightVec, -1);
    if (right) moveDir?.addScaledVector(rightVec, 1);

    if (moveDir?.length() > 0) moveDir?.normalize();

    const displacement = moveDir?.multiplyScalar(speed * clampedDelta);
    const nextPos = camera?.position?.clone()?.add(displacement);

    if (up) nextPos.y += verticalSpeed * clampedDelta;
    if (down) nextPos.y -= verticalSpeed * clampedDelta;

    if (modelBounds?.current) {
      const margin = 0.3;
      const maxY = modelBounds?.current?.max?.y + 40;
      const minY = modelBounds?.current?.min?.y + 0.3;
      nextPos.x = Math.max(modelBounds?.current?.min?.x + margin, Math.min(modelBounds?.current?.max?.x - margin, nextPos?.x));
      nextPos.z = Math.max(modelBounds?.current?.min?.z + margin, Math.min(modelBounds?.current?.max?.z - margin, nextPos?.z));
      nextPos.y = Math.max(minY, Math.min(maxY, nextPos?.y));
    }

    if (!up && !down) {
      nextPos.y = camera?.position?.y;
    }

    camera?.position?.copy(nextPos);
  });

  return null;
};

// ─── Mobile Fullscreen Button ─────────────────────────────────────────────────
const MobileFullscreenButton = ({ containerRef, isCSSFullscreen, onToggleCSSFullscreen }) => {
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);

  const supportsNativeFullscreen = !!(
    document.documentElement?.requestFullscreen ||
    document.documentElement?.webkitRequestFullscreen ||
    document.documentElement?.mozRequestFullScreen
  );

  useEffect(() => {
    if (!supportsNativeFullscreen) return;
    const onFSChange = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement);
      setIsNativeFullscreen(fs);
    };
    document.addEventListener('fullscreenchange', onFSChange);
    document.addEventListener('webkitfullscreenchange', onFSChange);
    document.addEventListener('mozfullscreenchange', onFSChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFSChange);
      document.removeEventListener('webkitfullscreenchange', onFSChange);
      document.removeEventListener('mozfullscreenchange', onFSChange);
    };
  }, [supportsNativeFullscreen]);

  const isFullscreen = supportsNativeFullscreen ? isNativeFullscreen : isCSSFullscreen;

  const toggleFullscreen = useCallback(async (e) => {
    e?.stopPropagation();
    try {
      if (supportsNativeFullscreen) {
        if (!isNativeFullscreen) {
          const el = containerRef?.current || document.documentElement;
          if (el?.requestFullscreen) await el?.requestFullscreen();
          else if (el?.webkitRequestFullscreen) await el?.webkitRequestFullscreen();
          else if (el?.mozRequestFullScreen) await el?.mozRequestFullScreen();
          try {
            if (screen?.orientation?.lock) await screen.orientation?.lock('landscape');
            else if (window?.screen?.lockOrientation) window.screen?.lockOrientation('landscape');
          } catch (_) {}
        } else {
          if (document.exitFullscreen) await document.exitFullscreen();
          else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
          else if (document.mozCancelFullScreen) await document.mozCancelFullScreen();
          try { if (screen?.orientation?.unlock) screen.orientation?.unlock(); } catch (_) {}
        }
      } else {
        onToggleCSSFullscreen?.();
      }
    } catch (_) {
      onToggleCSSFullscreen?.();
    }
  }, [isNativeFullscreen, supportsNativeFullscreen, containerRef, onToggleCSSFullscreen]);

  return (
    <button
      onTouchStart={(e) => { e?.stopPropagation(); }}
      onClick={toggleFullscreen}
      style={{
        position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(5,5,5,0.85)', border: '1px solid rgba(212,175,55,0.4)',
        borderRadius: '20px', padding: '10px 20px', color: '#D4AF37', fontSize: '13px',
        fontFamily: 'DM Sans, sans-serif', cursor: 'pointer', backdropFilter: 'blur(12px)',
        zIndex: 60, display: 'flex', alignItems: 'center', gap: '8px', letterSpacing: '0.05em',
        boxShadow: '0 4px 15px rgba(0,0,0,0.5)', touchAction: 'manipulation', fontWeight: 'bold'
      }}
    >
      {isFullscreen ? (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
          </svg>
          Réduire
        </>
      ) : (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
          </svg>
          Plein écran
        </>
      )}
    </button>
  );
};

// ─── Mobile Joystick ──────────────────────────────────────────────────────────
const MobileJoystick = ({ moveState }) => {
  const baseRef = useRef(null);
  const knobRef = useRef(null);
  const touchId = useRef(null);
  const basePos = useRef({ x: 0, y: 0 });
  const maxRadius = 44;
  const prevDir = useRef({ forward: false, backward: false, left: false, right: false });

  const resetKnob = useCallback(() => {
    if (knobRef?.current) {
      knobRef.current.style.transform = 'translate(-50%, -50%)';
      knobRef.current.style.background = 'rgba(212,175,55,0.8)';
    }
    moveState.current.forward = false;
    moveState.current.backward = false;
    moveState.current.left = false;
    moveState.current.right = false;
    prevDir.current = { forward: false, backward: false, left: false, right: false };
    touchId.current = null;
  }, [moveState]);

  const handleTouchStart = useCallback((e) => {
    e?.stopPropagation();
    const touch = e?.changedTouches?.[0];
    touchId.current = touch?.identifier;
    const rect = baseRef?.current?.getBoundingClientRect();
    if (rect) {
      basePos.current = { x: rect?.left + rect?.width / 2, y: rect?.top + rect?.height / 2 };
    }
    haptic?.light();
  }, []);

  const handleTouchMove = useCallback((e) => {
    e?.stopPropagation();
    const touch = Array.from(e?.changedTouches)?.find(t => t?.identifier === touchId?.current);
    if (!touch) return;

    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const edgeMargin = 60;
    const distFromLeft = touch?.clientX;
    const distFromRight = screenW - touch?.clientX;
    const distFromTop = touch?.clientY;
    const distFromBottom = screenH - touch?.clientY;
    const minEdgeDist = Math.min(distFromLeft, distFromRight, distFromTop, distFromBottom);
    const dampFactor = minEdgeDist < edgeMargin ? 0.35 + 0.65 * (minEdgeDist / edgeMargin) : 1.0;

    const rawDx = touch?.clientX - basePos?.current?.x;
    const rawDy = touch?.clientY - basePos?.current?.y;
    const dx = rawDx * dampFactor;
    const dy = rawDy * dampFactor;

    const dist = Math.min(Math.sqrt(dx * dx + dy * dy), maxRadius);
    const angle = Math.atan2(dy, dx);

    const kx = Math.cos(angle) * dist;
    const ky = Math.sin(angle) * dist;

    if (knobRef?.current) {
      knobRef.current.style.transform = `translate(calc(-50% + ${kx}px), calc(-50% + ${ky}px))`;
      const intensity = dist / maxRadius;
      // Change color slightly when active
      knobRef.current.style.background = intensity > 0.5 ? '#D4AF37' : 'rgba(212,175,55,0.8)';
    }

    const threshold = maxRadius * 0.22;
    const newForward = ky < -threshold;
    const newBackward = ky > threshold;
    const newLeft = kx < -threshold;
    const newRight = kx > threshold;

    const prev = prevDir?.current;
    if (newForward !== prev?.forward || newBackward !== prev?.backward ||
        newLeft !== prev?.left || newRight !== prev?.right) {
      if (newForward || newBackward || newLeft || newRight) {
        haptic?.selection();
      }
    }

    moveState.current.forward = newForward;
    moveState.current.backward = newBackward;
    moveState.current.left = newLeft;
    moveState.current.right = newRight;
    prevDir.current = { forward: newForward, backward: newBackward, left: newLeft, right: newRight };
  }, [moveState]);

  const handleTouchEnd = useCallback((e) => {
    e?.stopPropagation();
    resetKnob();
  }, [resetKnob]);

  const isSmallScreen = window.innerWidth < 390;

  return (
    <div
      ref={baseRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'absolute',
        bottom: isSmallScreen ? '100px' : '80px',
        left: isSmallScreen ? '20px' : '28px',
        width: isSmallScreen ? '110px' : '100px',
        height: isSmallScreen ? '110px' : '100px',
        borderRadius: '50%',
        background: 'rgba(5,5,5,0.5)',
        border: '2px solid rgba(212,175,55,0.4)',
        backdropFilter: 'blur(12px)',
        touchAction: 'none',
        zIndex: 50,
        boxShadow: '0 0 20px rgba(0,0,0,0.5)',
        animation: 'joystick-idle 3s ease-in-out infinite',
      }}
    >
      <div
        ref={knobRef}
        style={{
          position: 'absolute', top: '50%', left: '50%',
          width: isSmallScreen ? '50px' : '44px', height: isSmallScreen ? '50px' : '44px',
          borderRadius: '50%', background: 'rgba(212,175,55,0.8)',
          transform: 'translate(-50%, -50%)', boxShadow: '0 0 15px rgba(212,175,55,0.4)',
          pointerEvents: 'none', transition: 'background 0.1s ease',
        }}
      />
      {['↑','↓','←','→']?.map((arrow, i) => {
        const positions = [
          { top: '8px', left: '50%', transform: 'translateX(-50%)' },
          { bottom: '8px', left: '50%', transform: 'translateX(-50%)' },
          { left: '8px', top: '50%', transform: 'translateY(-50%)' },
          { right: '8px', top: '50%', transform: 'translateY(-50%)' },
        ];
        return (
          <span key={arrow} style={{
            position: 'absolute', ...positions?.[i], color: 'rgba(212,175,55,0.5)',
            fontSize: '12px', pointerEvents: 'none', fontFamily: 'DM Sans, sans-serif', fontWeight: 'bold'
          }}>{arrow}</span>
        );
      })}
    </div>
  );
};

// ─── Mobile Vertical Buttons (Up / Down / Door) ───────────────────────────────
const MobileVerticalControls = ({ moveState, onOpenDoor }) => {
  const isSmallScreen = window.innerWidth < 390;
  return (
    <div style={{
      position: 'absolute', bottom: isSmallScreen ? '100px' : '80px', right: isSmallScreen ? '16px' : '24px',
      display: 'flex', flexDirection: 'column', gap: isSmallScreen ? '12px' : '10px', zIndex: 50,
    }}>
      <button
        onTouchStart={(e) => { e?.stopPropagation(); moveState.current.up = true; haptic?.light(); }}
        onTouchEnd={(e) => { e?.stopPropagation(); moveState.current.up = false; }}
        style={{
          width: isSmallScreen ? '54px' : '50px', height: isSmallScreen ? '54px' : '50px',
          borderRadius: '16px', background: 'rgba(5,5,5,0.6)', border: '1px solid rgba(212,175,55,0.5)',
          color: '#D4AF37', fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          touchAction: 'none', cursor: 'pointer', backdropFilter: 'blur(12px)', boxShadow: '0 4px 15px rgba(0,0,0,0.5)',
        }}
      >↑</button>
      <button
        onTouchStart={(e) => { e?.stopPropagation(); onOpenDoor(); haptic?.medium(); }}
        style={{
          width: isSmallScreen ? '54px' : '50px', height: isSmallScreen ? '54px' : '50px',
          borderRadius: '16px', background: 'rgba(212,175,55,0.15)', border: '1px solid #D4AF37',
          color: '#D4AF37', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          touchAction: 'none', cursor: 'pointer', backdropFilter: 'blur(12px)', boxShadow: '0 0 20px rgba(212,175,55,0.2)',
        }}
      >🚪</button>
      <button
        onTouchStart={(e) => { e?.stopPropagation(); moveState.current.down = true; haptic?.light(); }}
        onTouchEnd={(e) => { e?.stopPropagation(); moveState.current.down = false; }}
        style={{
          width: isSmallScreen ? '54px' : '50px', height: isSmallScreen ? '54px' : '50px',
          borderRadius: '16px', background: 'rgba(5,5,5,0.6)', border: '1px solid rgba(212,175,55,0.5)',
          color: '#D4AF37', fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          touchAction: 'none', cursor: 'pointer', backdropFilter: 'blur(12px)', boxShadow: '0 4px 15px rgba(0,0,0,0.5)',
        }}
      >↓</button>
    </div>
  );
};

// ─── Keyboard Controls ────────────────────────────────────────────────────────
const useKeyboardControls = (moveState) => {
  useEffect(() => {
    const keyMap = {
      KeyW: 'forward', KeyZ: 'forward', ArrowUp: 'forward',
      KeyS: 'backward', ArrowDown: 'backward',
      KeyA: 'left', KeyQ: 'left', ArrowLeft: 'left',
      KeyD: 'right', ArrowRight: 'right',
      KeyE: 'up', Space: 'up',
      KeyC: 'down', ControlLeft: 'down', ControlRight: 'down',
    };

    const onKeyDown = (e) => {
      const dir = keyMap?.[e?.code];
      if (dir) { e?.preventDefault(); moveState.current[dir] = true; }
      if (e?.code === 'KeyF') { e?.preventDefault(); window.__viewer3d_openDoor?.(); }
    };
    const onKeyUp = (e) => {
      const dir = keyMap?.[e?.code];
      if (dir) moveState.current[dir] = false;
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [moveState]);
};

// ─── Quality Tier Definitions ─────────────────────────────────────────────────
const QUALITY_TIERS = {
  low: { label: 'Bas', dpr: 0.75, antialias: false, shadowMapSize: 128, maxTextureSize: 256, shadows: false, description: '2G / Économie batterie' },
  medium: { label: 'Moyen', dpr: 1.0, antialias: false, shadowMapSize: 512, maxTextureSize: 1024, shadows: true, description: '3G / Équilibré' },
  high: { label: 'Élevé', dpr: 1.5, antialias: true, shadowMapSize: 1024, maxTextureSize: 2048, shadows: true, description: '4G / Haute qualité' },
  ultra: { label: 'Ultra', dpr: Math.min(window.devicePixelRatio || 1, 2.0), antialias: true, shadowMapSize: 2048, maxTextureSize: 4096, shadows: true, description: 'WiFi / Qualité maximale' },
};

const detectOptimalQuality = () => {
  try {
    const conn = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
    const isMobile = /iPhone|iPad|iPod|Android/i?.test(navigator?.userAgent || '');
    const pixelRatio = window?.devicePixelRatio || 1;
    const cores = navigator?.hardwareConcurrency || 2;
    const memory = navigator?.deviceMemory || 2;

    let score = 5;
    if (conn) {
      const { effectiveType, downlink, saveData, rtt } = conn;
      if (saveData) return 'low';
      if (effectiveType === 'slow-2g') return 'low';
      if (effectiveType === '2g') score -= 3;
      else if (effectiveType === '3g') score -= 1;
      else if (effectiveType === '4g') score += 1;

      if (downlink !== undefined) {
        if (downlink < 0.5) score -= 3;
        else if (downlink < 1.5) score -= 1;
        else if (downlink >= 10) score += 2;
        else if (downlink >= 5) score += 1;
      }
    }

    if (isMobile) score -= 1;
    if (cores >= 8) score += 2;
    else if (cores <= 2) score -= 1;
    if (memory >= 8) score += 2;
    else if (memory <= 1) score -= 2;
    if (pixelRatio >= 3) score += 1;

    if (score <= 2) return 'low';
    if (score <= 5) return 'medium';
    if (score <= 7) return 'high';
    return 'ultra';
  } catch {
    return 'medium';
  }
};

// ─── Quality Selector UI ──────────────────────────────────────────────────────
const QualitySelector = ({ quality, onQualityChange, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [autoDetected, setAutoDetected] = useState(false);

  useEffect(() => {
    setAutoDetected(true);
    const t = setTimeout(() => setAutoDetected(false), 3000);
    return () => clearTimeout(t);
  }, []);

  if (isLoading) return null;

  const current = QUALITY_TIERS?.[quality];
  const tiers = ['low', 'medium', 'high', 'ultra'];
  const tierIndex = tiers?.indexOf(quality);
  const barColors = ['#ef4444', '#f59e0b', '#22c55e', '#D4AF37'];
  const barColor = barColors?.[tierIndex] || '#D4AF37';

  return (
    <div style={{ position: 'absolute', top: '56px', left: '16px', zIndex: 45, fontFamily: 'DM Sans, sans-serif' }}>
      {autoDetected && (
        <div style={{
          position: 'absolute', top: '-30px', left: 0, background: 'rgba(5,5,5,0.9)', border: '1px solid rgba(212,175,55,0.5)',
          borderRadius: '8px', padding: '4px 10px', color: '#D4AF37', fontSize: '10px', letterSpacing: '0.05em', whiteSpace: 'nowrap',
          animation: 'fadeInOut 3s ease forwards', boxShadow: '0 4px 10px rgba(0,0,0,0.5)', fontWeight: 'bold'
        }}>
          ✦ QUALITÉ DÉTECTÉE AUTO
        </div>
      )}
      <button
        onClick={() => setIsOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(5,5,5,0.85)',
          border: `1px solid ${isOpen ? barColor : 'rgba(255,255,255,0.15)'}`, borderRadius: '12px', padding: '8px 14px',
          color: '#fff', fontSize: '12px', cursor: 'pointer', backdropFilter: 'blur(12px)', transition: 'all 0.3s ease',
          boxShadow: '0 4px 15px rgba(0,0,0,0.5)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '14px' }}>
          {[4, 8, 11, 14]?.map((h, i) => (
            <div key={i} style={{ width: '3px', height: `${h}px`, borderRadius: '1px', background: i <= tierIndex ? barColor : 'rgba(255,255,255,0.2)', transition: 'background 0.3s ease' }} />
          ))}
        </div>
        <span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 'bold', letterSpacing: '0.05em' }}>{current?.label}</span>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0, background: 'rgba(5,5,5,0.95)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px', overflow: 'hidden', backdropFilter: 'blur(20px)', boxShadow: '0 10px 40px rgba(0,0,0,0.8)', minWidth: '220px'
        }}>
          <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)', color: 'rgba(212,175,55,0.8)', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 'bold' }}>
            Qualité de rendu 3D
          </div>
          {tiers?.map((tier, i) => {
            const t = QUALITY_TIERS?.[tier];
            const isActive = tier === quality;
            const bc = barColors?.[i];
            return (
              <button
                key={tier}
                onClick={() => { onQualityChange(tier); setIsOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '12px 16px',
                  background: isActive ? 'rgba(212,175,55,0.1)' : 'transparent', border: 'none',
                  borderLeft: isActive ? `3px solid ${bc}` : '3px solid transparent',
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.6)', fontSize: '13px', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s ease', fontFamily: 'DM Sans, sans-serif'
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '12px', flexShrink: 0 }}>
                  {[3, 6, 9, 12]?.map((h, bi) => (
                    <div key={bi} style={{ width: '3px', height: `${h}px`, borderRadius: '1px', background: bi <= i ? bc : 'rgba(255,255,255,0.15)' }} />
                  ))}
                </div>
                <div>
                  <div style={{ fontWeight: isActive ? 'bold' : 'normal', fontSize: '13px' }}>{t?.label}</div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginTop: '2px' }}>{t?.description}</div>
                </div>
                {isActive && <div style={{ marginLeft: 'auto', color: bc, fontSize: '12px', fontWeight: 'bold' }}>✓</div>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main Viewer3D Component ──────────────────────────────────────────────────
const Viewer3D = ({ modelUrl, onExit }) => {
  const moveState = useRef({ forward: false, backward: false, left: false, right: false, up: false, down: false });
  const containerRef = useRef(null);
  const [modelScene, setModelScene] = useState(null);
  const [isMobile] = useState(() => /iPhone|iPad|iPod|Android/i?.test(navigator.userAgent));
  const [nearDoor, setNearDoor] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [networkQuality] = useState(() => getNetworkQuality());
  const [isCSSFullscreen, setIsCSSFullscreen] = useState(false);
  const [perfMetrics, setPerfMetrics] = useState({ fps: null, memMB: null });
  const [modelLoadTime, setModelLoadTime] = useState(null);
  const loadStartTime = useRef(null);
  const [quality, setQuality] = useState(() => detectOptimalQuality());

  const handlePerfMetrics = useCallback(({ fps, memMB }) => { setPerfMetrics({ fps, memMB }); }, []);
  const renderSettings = QUALITY_TIERS?.[quality] || QUALITY_TIERS?.medium;
  const [resolvedModelUrl, setResolvedModelUrl] = useState(null);

  useEffect(() => {
    if (!modelUrl) return;
    const cached = resolveAssetUrl(modelUrl);
    setResolvedModelUrl(cached || modelUrl);
  }, [modelUrl]);

  useEffect(() => { if (modelScene) applyTextureOptimizations(modelScene, quality); }, [quality, modelScene]);
  useEffect(() => { return () => { if (modelScene) disposeScene(modelScene); }; }, [modelScene]);

  useEffect(() => {
    return () => {
      if (!isMobile) return;
      try {
        const isFS = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement);
        if (isFS) {
          if (document.exitFullscreen) document.exitFullscreen();
          else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
          else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
        }
        if (screen?.orientation?.unlock) screen.orientation?.unlock();
      } catch (_) {}
    };
  }, [isMobile]);

  useKeyboardControls(moveState);

  const handleOpenDoor = useCallback(() => { window.__viewer3d_openDoor?.(); }, []);
  const handleProgress = useCallback((p) => {
    if (p > 0 && loadStartTime?.current === null) loadStartTime.current = performance.now();
    setLoadProgress(p);
  }, []);
  const handleLoaded = useCallback(() => {
    if (loadStartTime?.current !== null) setModelLoadTime(performance.now() - loadStartTime?.current);
    setTimeout(() => setIsLoading(false), 400);
  }, []);

  const handleToggleCSSFullscreen = useCallback(() => {
    setIsCSSFullscreen(prev => {
      const next = !prev;
      try {
        if (next) {
          if (screen?.orientation?.lock) screen.orientation?.lock('landscape')?.catch(() => {});
          else if (window?.screen?.lockOrientation) window.screen?.lockOrientation('landscape');
        } else {
          if (screen?.orientation?.unlock) screen.orientation?.unlock();
        }
      } catch (_) {}
      return next;
    });
  }, []);

  const dpr = renderSettings?.dpr;
  const shadowMapSize = renderSettings?.shadowMapSize;

  const containerStyle = isCSSFullscreen
    ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', zIndex: 9999, background: '#050505' }
    : { position: 'relative', width: '100%', height: '100%', background: '#050505' };

  return (
    <div ref={containerRef} style={containerStyle}>
      {networkQuality === 'slow' && !isLoading && (
        <div style={{
          position: 'absolute', top: '56px', right: '16px', background: 'rgba(5,5,5,0.85)',
          border: '1px solid rgba(212,175,55,0.4)', borderRadius: '8px', padding: '6px 12px',
          color: '#D4AF37', fontSize: '11px', fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.05em',
          zIndex: 45, pointerEvents: 'none', fontWeight: 'bold', boxShadow: '0 4px 10px rgba(0,0,0,0.5)'
        }}>
          ⚡ Mode économie réseau
        </div>
      )}
      
      <QualitySelector quality={quality} onQualityChange={setQuality} isLoading={isLoading} />
      
      <Canvas
        shadows={renderSettings?.shadows}
        dpr={dpr}
        camera={{ fov: 75, near: 0.05, far: 1000, position: [0, 1.7, 0] }}
        gl={{
          antialias: renderSettings?.antialias,
          powerPreference: quality === 'ultra' ? 'high-performance' : quality === 'low' ? 'low-power' : 'default',
          stencil: false, depth: true,
        }}
        performance={{ min: 0.5 }}
        style={{ width: '100%', height: '100%' }}
      >
        <ProgressTracker onProgress={handleProgress} onLoaded={handleLoaded} />
        {isMobile && <PerformanceMonitor onMetrics={handlePerfMetrics} />}

        <ambientLight intensity={0.8} />
        <directionalLight position={[10, 20, 10]} intensity={1.2} castShadow={renderSettings?.shadows} shadow-mapSize-width={shadowMapSize} shadow-mapSize-height={shadowMapSize} />
        <pointLight position={[-5, 5, -5]} intensity={0.4} color="#D4AF37" />
        <hemisphereLight args={['#b1e1ff', '#b97a20', 0.3]} />
        <Sky sunPosition={[100, 20, 100]} />

        <Suspense fallback={null}>
          {resolvedModelUrl && <GLBModel url={resolvedModelUrl} onLoaded={setModelScene} renderQuality={quality} />}
        </Suspense>

        <FPSController moveState={moveState} modelScene={modelScene} />
        {modelScene && <DoorSystem modelScene={modelScene} onDoorHint={setNearDoor} />}
      </Canvas>

      <LoadingScreen progress={loadProgress} isVisible={isLoading} />
      
      {isMobile && !isLoading && <MobileJoystick moveState={moveState} />}
      {isMobile && !isLoading && <MobilePerformanceOverlay fps={perfMetrics?.fps} memMB={perfMetrics?.memMB} networkQuality={networkQuality} modelLoadTime={modelLoadTime} />}
      {isMobile && !isLoading && <MobileVerticalControls moveState={moveState} onOpenDoor={handleOpenDoor} />}
      {isMobile && <MobileFullscreenButton containerRef={containerRef} isCSSFullscreen={isCSSFullscreen} onToggleCSSFullscreen={handleToggleCSSFullscreen} />}
      
      {nearDoor && !isLoading && (
        <div style={{
          position: 'absolute', bottom: isMobile ? '180px' : '90px', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(5,5,5,0.9)', border: '1px solid rgba(212,175,55,0.6)', borderRadius: '20px',
          padding: '10px 24px', color: '#D4AF37', fontSize: '14px', fontFamily: 'DM Sans, sans-serif',
          letterSpacing: '0.05em', backdropFilter: 'blur(16px)', zIndex: 45, display: 'flex', alignItems: 'center', gap: '10px',
          animation: 'pulse 1.5s ease-in-out infinite', pointerEvents: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', fontWeight: 'bold'
        }}>
          <span style={{ fontSize: '18px' }}>🚪</span>
          {isMobile ? 'Appuyez sur 🚪 pour ouvrir' : 'Appuyez sur [F] pour ouvrir la porte'}
        </div>
      )}

      {!isLoading && (
        <div style={{
          position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(5,5,5,0.85)',
          border: '1px solid rgba(212,175,55,0.4)', borderRadius: '30px', padding: '8px 20px',
          backdropFilter: 'blur(16px)', zIndex: 40, boxShadow: '0 4px 15px rgba(0,0,0,0.5)'
        }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#D4AF37', boxShadow: '0 0 10px #D4AF37' }} />
          <span style={{ color: '#D4AF37', fontSize: '12px', fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.15em', fontWeight: 'bold' }}>
            VISITE KPS ENGINE
          </span>
        </div>
      )}

      {!isMobile && !isLoading && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 40 }}>
          <div style={{ width: '24px', height: '2px', background: 'rgba(212,175,55,0.8)', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '2px' }} />
          <div style={{ width: '2px', height: '24px', background: 'rgba(212,175,55,0.8)', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '2px' }} />
        </div>
      )}

      {!isMobile && !isLoading && (
        <div style={{
          position: 'absolute', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: '16px', background: 'rgba(5,5,5,0.8)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px', padding: '12px 24px', backdropFilter: 'blur(16px)', zIndex: 40, boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
        }}>
          {[
            ['Z/W/↑ S/↓', 'Avancer/Reculer'], ['Q/A/← D/→', 'Gauche/Droite'], ['E/Espace', 'Monter'],
            ['C/Ctrl', 'Descendre'], ['F', 'Ouvrir porte'], ['Clic + Souris', 'Regarder'],
          ]?.map(([key, label]) => (
            <div key={key} style={{ textAlign: 'center' }}>
              <div style={{ color: '#D4AF37', fontSize: '11px', fontFamily: 'DM Sans, sans-serif', fontWeight: 800, letterSpacing: '0.05em' }}>{key}</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', fontFamily: 'DM Sans, sans-serif', marginTop: '2px' }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onExit}
        style={{
          position: 'absolute', top: '16px', right: '16px', background: 'rgba(5,5,5,0.85)',
          border: '1px solid rgba(212,175,55,0.5)', borderRadius: '14px', padding: '10px 20px',
          color: '#D4AF37', fontSize: '13px', fontFamily: 'DM Sans, sans-serif', fontWeight: 'bold',
          cursor: 'pointer', backdropFilter: 'blur(16px)', zIndex: 250, display: 'flex', alignItems: 'center',
          gap: '8px', letterSpacing: '0.05em', boxShadow: '0 4px 15px rgba(0,0,0,0.5)', transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#D4AF37'; e.currentTarget.style.color = '#050505'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(5,5,5,0.85)'; e.currentTarget.style.color = '#D4AF37'; }}
      >
        <span style={{ fontSize: '16px' }}>✕</span> Quitter
      </button>
      
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; transform: translateX(-50%) scale(1); } 50% { opacity: 0.75; transform: translateX(-50%) scale(0.97); } }
        @keyframes joystick-idle { 0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.25); } 50% { box-shadow: 0 0 0 8px rgba(212,175,55,0.08); } }
        @keyframes fadeInOut { 0% { opacity: 0; transform: translateY(4px); } 15% { opacity: 1; transform: translateY(0); } 75% { opacity: 1; } 100% { opacity: 0; } }
      `}</style>
    </div>
  );
};

export default Viewer3D;